import { Layout } from '@/components/Layout';
import { VaultStepCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { WHATSAPP_NUMBER, ROUTE_PATHS } from '@/lib/index';
import { Shield, Lock, CheckCircle, MessageCircle, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';

const VAULT_STEPS = [
  {
    step: 1,
    title: 'Choose Your Property',
    description: 'Select from our curated properties or bring your own development project to us.',
  },
  {
    step: 2,
    title: 'Sign The Vault Agreement',
    description: 'We formalize the partnership with a comprehensive agreement outlining all terms and protections.',
  },
  {
    step: 3,
    title: 'Money Locked in Escrow',
    description: 'Your investment is secured in a bank escrow account — untouchable until completion.',
  },
  {
    step: 4,
    title: 'Architecture & Construction',
    description: 'Our team handles all design, permits, and construction management from start to finish.',
  },
  {
    step: 5,
    title: 'Legal Process Completion',
    description: 'We navigate all Tanzania and Zanzibar legal requirements, ensuring full compliance.',
  },
  {
    step: 6,
    title: 'Keys Delivered, Funds Released',
    description: 'Upon verified completion, you receive your property and escrow funds are released.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'What is The Vault System™?',
    answer: 'The Vault System™ is our proprietary escrow-based investment protection framework. Your money is locked in a bank escrow account and only released upon verified completion of all legal and construction requirements. This eliminates risk and ensures guaranteed delivery.',
  },
  {
    question: 'How is my money protected?',
    answer: 'Your investment is held in a secure bank escrow account throughout the entire process. The funds cannot be accessed by anyone until all contractual obligations are met, legal processes are completed, and the property is ready for handover. This provides 100% financial protection.',
  },
  {
    question: 'What legal requirements do you handle?',
    answer: 'We manage all Tanzania and Zanzibar legal requirements including land title verification, building permits, zoning approvals, environmental clearances, and final property registration. Our legal team ensures full compliance with local regulations.',
  },
  {
    question: 'Can I bring my own project?',
    answer: 'Yes! You can either select from our curated properties or bring your own development project. We provide the same Vault System™ protection, architecture services, and construction management regardless of the property source.',
  },
  {
    question: 'How long does the process take?',
    answer: 'Timeline varies based on project scope. Typical villa construction takes 8-12 months, while plot purchases with legal processing take 3-6 months. We provide detailed timelines during the agreement phase and keep you updated throughout.',
  },
];

export default function Investment() {
  return (
    <Layout>
      <div className="relative min-h-screen bg-primary">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${IMAGES.INVEST_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-primary" />

        <div className="relative z-10">
          <section className="container mx-auto px-4 py-24 md:py-32">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-6">
                The Vault System™
              </h1>
              <p className="text-xl md:text-2xl text-primary-foreground/90 font-light">
                How Your Investment is Protected
              </p>
            </div>
          </section>

          <section className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto">
              <div className="bg-card/10 backdrop-blur-xl border border-accent/20 rounded-2xl p-8 md:p-12">
                <h2 className="text-3xl font-bold text-primary-foreground mb-6">
                  Guaranteed Payment. Zero Risk.
                </h2>
                <p className="text-lg text-primary-foreground/80 leading-relaxed mb-6">
                  The Vault System™ is our revolutionary escrow-based investment protection framework. Whether you choose one of our curated properties or bring your own development project, we handle everything from architecture to construction while your money stays locked in a secure bank escrow account.
                </p>
                <p className="text-lg text-primary-foreground/80 leading-relaxed mb-6">
                  Here's how it works: You select your property or project. We sign a comprehensive Vault Agreement outlining all terms. Your investment is immediately transferred to a bank escrow account — untouchable by anyone until completion. We manage all architecture, permits, construction, and legal processes. Only when everything is verified complete and you receive your keys does the escrow release funds.
                </p>
                <p className="text-lg text-primary-foreground/80 leading-relaxed">
                  This means your money is never at risk. You get guaranteed delivery. We get paid only when you're satisfied. It's real estate investment the way it should be.
                </p>
              </div>
            </div>
          </section>

          <section className="container mx-auto px-4 py-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-primary-foreground mb-12">
                The 6-Step Vault Process
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {VAULT_STEPS.map((step) => (
                  <VaultStepCard
                    key={step.step}
                    step={step.step}
                    title={step.title}
                    description={step.description}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="container mx-auto px-4 py-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-primary-foreground mb-12">
                Why Choose The Vault System
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-card/10 backdrop-blur-xl border border-accent/20 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-2xl font-bold text-primary-foreground mb-4">
                    100% Money Protection
                  </h3>
                  <p className="text-primary-foreground/80 leading-relaxed">
                    Your investment is held in a secure bank escrow account throughout the entire process. Funds are never at risk and only released upon verified completion.
                  </p>
                </div>

                <div className="bg-card/10 backdrop-blur-xl border border-accent/20 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Lock className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-2xl font-bold text-primary-foreground mb-4">
                    Full Legal Compliance
                  </h3>
                  <p className="text-primary-foreground/80 leading-relaxed">
                    We handle all Tanzania and Zanzibar legal requirements including permits, title verification, and property registration. You stay compliant without the hassle.
                  </p>
                </div>

                <div className="bg-card/10 backdrop-blur-xl border border-accent/20 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-2xl font-bold text-primary-foreground mb-4">
                    Architecture to Keys
                  </h3>
                  <p className="text-primary-foreground/80 leading-relaxed">
                    From initial design to final handover, we manage every aspect of construction. You get a turnkey property without managing contractors or timelines.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-primary-foreground mb-12">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {FAQ_ITEMS.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-card/10 backdrop-blur-xl border border-accent/20 rounded-xl px-6"
                  >
                    <AccordionTrigger className="text-lg font-semibold text-primary-foreground hover:text-accent">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-primary-foreground/80 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          <section className="container mx-auto px-4 py-16 pb-24">
            <div className="max-w-4xl mx-auto text-center">
              <div className="bg-card/10 backdrop-blur-xl border border-accent/20 rounded-2xl p-12">
                <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
                  Ready to Invest?
                </h2>
                <p className="text-lg text-primary-foreground/80 mb-8">
                  Start your secure investment journey with The Vault System™. Contact us today to discuss your property goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    asChild
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                  >
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=Hi, I'm interested in The Vault System™`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp Us
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-accent text-primary-foreground hover:bg-accent/10"
                  >
                    <Link to={ROUTE_PATHS.CONTACT}>
                      <Mail className="w-5 h-5 mr-2" />
                      Contact Form
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}